<template>
  <div>
    <filter-panel
      class="filters-panel"
      :apply-filter-immediately="false"
      :filter-parameters="formattedFilters"
      @update="applyFilter"
      :notification-text="null"
      :api-request-data="apiRequestData"
    >
      <template #panel-actions>
        <div class="actions">
          <b-button
            @click="vesselsModal.show = true"
            class="action-button"
            size="sm"
            variant="info"
          >
            <span class="text-nowrap">
              List of vessels
            </span>
          </b-button>

          <b-button
            @click="vesselsMapModal.show = true"
            class="action-button"
            size="sm"
            variant="info"
          >
            <span class="text-nowrap">
              Live map
            </span>
          </b-button>
        </div>
      </template>
    </filter-panel>

    <vessels-modal
      v-model="vesselsModal.show"
      :filter-values="apiFilterValues"
    />

    <vessels-map-modal
      v-model="vesselsMapModal.show"
      :required-filter-fields="vesselsMapModal.requiredFilterFields"
      :filter-values="mapFilterValues"
    />
  </div>
</template>

<script>
import getNested from "lodash-es/get";
import pickBy from "lodash-es/pickBy";
import sortBy from "lodash-es/sortBy";
import { mapActions, mapGetters } from "vuex";
import {
  getAPIFilterValues,
  applyFilterValues,
  getFormattedFilters
} from "../widgets/FilterPanel";

import FilterPanel from "../widgets/FilterPanel";
import VesselsModal from "./VesselsModal";
import VesselsMapModal from "@/components/widgets/VesselsMapModal";

export default {
  name: "filters",

  components: {
    VesselsMapModal,
    VesselsModal,
    FilterPanel
  },

  props: {
    apiRequestData: {
      type: Object,
      default: () => ({
        // it's possible to have a few active requests at the same page
        emptySection: {
          method: "GET",
          url: "",
          headers: {},
          params: {},
          data: {}
        }
      })
    }
  },

  data() {
    const defaultChoices = {
      zone: [{ text: "-- Zone --", value: null }]
      // segment: [{text: 'Capesize', value: 'capesize'}],
      // direction: [{text: 'NNE', value: 'NNE'}],
      // ladenStatus: [{text: 'Laden', value: 'Laden'}],
    };

    return {
      filtersReady: false,

      vesselsModal: {
        show: false
      },

      vesselsMapModal: {
        show: false,
        requiredFilterFields: [
          "zoneId",
          "subSegment",
          "direction",
          "ladenStatus"
        ]
      },

      apiFilterValues: {},

      // select fields
      filterSchema: {
        zone: {
          name: "zone",
          apiName: "zoneId",
          type: "multiselect-region",
          placeholder: "Zone",
          value: [],
          choices: [],
          defaultChoice: defaultChoices.zone,
          options: {
            toggleAll: true,
          },
        },
        subSegment: {
          name: "subSegment",
          apiName: "subSegment",
          type: "treeselect",
          placeholder: "Segment",
          value: [],
          choices: [],
          rawChoices: [],
          defaultChoice: [],
          options: {
            toggleAll: true
          }
        },
        direction: {
          name: "direction",
          apiName: "direction",
          type: "multiselect-direction",
          placeholder: "Direction",
          value: [],
          choices: [
            { text: "NNE", value: "NNE" },
            { text: "ENE", value: "ENE" },
            { text: "ESE", value: "ESE" },
            { text: "SSE", value: "SSE" },
            { text: "SSW", value: "SSW" },
            { text: "WSW", value: "WSW" },
            { text: "WNW", value: "WNW" },
            { text: "NNW", value: "NNW" }
          ],
          defaultChoice: defaultChoices.direction
        },
        ladenStatus: {
          name: "ladenStatus",
          apiName: "ladenStatus",
          type: "multiselect",
          placeholder: "Laden status",

          value: [],
          choices: [
            { text: "Laden", value: "Laden" },
            { text: "Ballast", value: "Ballast" }
          ],
          defaultChoice: defaultChoices.ladenStatus
        },
        portStatus: {
          name: "portStatus",
          apiName: "portStatus",
          type: "multiselect",
          placeholder: "Port Status",

          value: [],
          choices: [
            { text: "In Port", value: "in_port" },
            { text: "At Sea", value: "at_sea" }
          ],
          defaultChoice: defaultChoices.portStatus
        },

        excludeMpv: {
          name: "excludeMpv",
          apiName: "excludeMpv",
          type: "checkbox",
          label: "Exclude Multi Purpose Vessels",
          value: true,
          defaultChoice: true
        }
      } // <-- filterParameters end
    };
  },

  methods: {
    ...mapActions("api/tradeflows", ["getSegments"]),

    ...mapActions("api/tonnage", ["getTonnageZonesWithPolygons"]),

    async loadFilters() {
      await this.loadSegmentChoices(), await this.loadZoneChoices();
      this.filtersReady = true;
    },

    async loadSegmentChoices() {
      const response = await this.getSegments({ group: true });
      this.filterSchema.subSegment.choices = response || [];
    },

    async loadZoneChoices() {
      // get tonnage zone names and assign first as selected in controls
      let choices = await this.getTonnageZonesWithPolygons();
      choices = choices.zones;
      if (choices) {
        this.filterSchema.zone.choices = choices.map(item => ({
          value: item.zoneId,
          text: item.zoneName,
          geoJSON: JSON.parse(item.geomPolygonGeojson)
        }));
        this.filterSchema.zone.choices = sortBy(
          this.filterSchema.zone.choices,
          "text"
        );
      } else {
        console.error("can't load choices");
      }
    },

    setDefaultValues() {
      this.filterSchema.zone.value = [this.filterSchema.zone.choices[0]];
    },

    async applyFilter(filterValues) {
      if (!this.filtersReady) {
        return;
      }

      applyFilterValues(this.filterSchema, filterValues);
      this.apiFilterValues = getAPIFilterValues(
        this.filterSchema,
        filterValues
      );
      this.$emit("search", this.apiFilterValues);
    }
  },

  computed: {
    ...mapGetters("api/presets", ["getActivePreset"]),

    formattedFilters() {
      return getFormattedFilters(this.filterSchema);
    },

    mapFilterValues() {
      return pickBy(this.apiFilterValues, (value, name) => {
        return this.vesselsMapModal.requiredFilterFields.includes(name);
      });
    }
  },

  watch: {
    getActivePreset: {
      immediate: true,
      handler: async function(preset) {
        if (preset && this.$route.path !== preset.viewName) {
          return;
        }

        if (!this.filtersReady) {
          await this.loadFilters();

          if (!preset) {
            this.setDefaultValues();
          }
        }

        if (preset) {
          const filterValues = getNested(preset, "params.filterPanel", {});
          applyFilterValues(this.filterSchema, filterValues);
        }
      }
    }
  }
};
</script>

<style scoped>
.filters-panel >>> .multiselect__tags {
  min-width: 300px;
}
</style>

<style scoped lang="scss">
.actions {
  margin-top: 5px;
  display: flex;

  .action-button:not(:last-child) {
    margin-right: 5px;
  }
}
</style>
